<script setup lang="ts">
import type {
  Accommodation,
  AccommodationBookInfo,
  AccommodationSearchResponse,
} from '../../../../../websites/types/AccommodationSearch'
import type { CampsiteResponse } from '../../../../../websites/types/Campsite'
import type { SortingOption } from '../../../../../websites/types/SortingOptions'

interface Props {
  searchPending: boolean
  siteData: CampsiteResponse | null
  searchData?: AccommodationSearchResponse | null
}

defineProps<Props>()

const accommodationStore = useAccommodationStore()
const searchStore = useSearchv2Store()
const configStore = useConfigStore()
const bookingStore = useBookingStore()

defineEmits([
  'refreshSearchData',
  'updateSelectedAccommodation',
  'showAccommodationInfo',
])
</script>

<template>
  <div class="container-xl">
    <p
      id="top"
      class="font-bold font-heading text-xl lg:text-3xl text-brand-secondary-800 pb-5 md:pb-7 pt-7 lg:pt-[50px]"
    >
      {{
        $t('accommodation.accommodation-and-prices.accommodation-and-prices')
      }}
    </p>
  </div>
  <div class="sticky top-0 z-10">
    <div class="lg:hidden">
      <LazyFilterMobile
        @update-filters="
          useUpdateQuery(),
            $emit('refreshSearchData'),
            scrollIntoViewWithOffset('top', 0)
        "
      >
        <LazyAccommodationFilterDrawer
          @update-filters="useUpdateQuery(), $emit('refreshSearchData')"
          @scroll-to-top="scrollIntoViewWithOffset('top', 0)"
        />
      </LazyFilterMobile>
    </div>

    <div class="hidden lg:block bg-brand-secondary-900">
      <div class="container-xl items-center py-6">
        <LazyAccommodationFilterA
          @update-filters="useUpdateQuery(), $emit('refreshSearchData')"
          @scroll-to-top="scrollIntoViewWithOffset('top', 0)"
        />
      </div>
    </div>
  </div>
  <div class="container-xl flex flex-col gap-xs pb-xs">
    <div
      class="flex flex-col lg:flex-row gap-md sm:gap-xs py-6 lg:items-center"
    >
      <div class="basis-2/5">
        <ResultCount
          class="text-brand-secondary-800 font-h4"
          :count="
            accommodationStore.getFilteredAlternatives.length +
            accommodationStore.getFilteredAccommodations.length
          "
          :loading="searchPending"
          :text="
            $t(
              'accommodation.accommodation-and-prices.accommodations-available',
              accommodationStore.getFilteredAccommodations.length
            )
          "
          :show-sub-result="siteData?.data.showPitchInfo"
          :sub-result-count="
            accommodationStore.getAllAvailableAccommodationPitches.length
          "
          :sub-result-text="
            $t(
              'accommodation.accommodation-and-prices.pitches-available',
              accommodationStore.getAllAvailableAccommodationPitches.length
            )
          "
        />
      </div>
      <div class="flex flex-col sm:flex-row gap-md sm:gap-xs w-full basis-3/5">
        <div class="basis-2/3">
          <div
            class="flex flex-col xs:flex-row gap-4 gap-x-12 items-start h-full xs:items-center"
          >
            <FormCheckbox
              v-if="siteData?.data.showPitchInfo"
              id="show-accommodations"
              class="items-center gap-xs"
              size="lg"
              :disabled="
                (accommodationStore.isPitchFilterActive &&
                  !accommodationStore.isAccTypeFilterActive) ||
                !accommodationStore.getAllAvailableAccommodationsWithoutPitches
                  .length
              "
              :label="
                $t('accommodation.accommodation-and-prices.show-accommodations')
              "
              :checked="accommodationStore.isAccTypeFilterActive"
              @update="
                (checked) => {
                  checked
                    ? accommodationStore.activateAccommodationFilters()
                    : accommodationStore.deactivateAccommodationFilters()
                }
              "
            />
            <FormCheckbox
              v-if="siteData?.data.showPitchInfo"
              id="show-pithces"
              class="items-center gap-xs"
              size="lg"
              :disabled="
                !accommodationStore.getAllAvailableAccommodationPitches
                  .length ||
                (accommodationStore.isAccTypeFilterActive &&
                  !accommodationStore.isPitchFilterActive)
              "
              :label="$t('accommodation.accommodation-and-prices.show-pitches')"
              :checked="accommodationStore.isPitchFilterActive"
              @update="accommodationStore.activatePitchFilter()"
            />
          </div>
        </div>
        <div class="basis-1/3">
          <FormDropdown
            v-model="searchStore.sortingOption"
            value="title"
            :items="configStore.configs.sortingOptions.accommodations.map((sortingOption: SortingOption) => {
            return {
              ...sortingOption,
              title: $t(`sorting.sortingTypes.${sortingOption.label}`),
            }
          })"
            @on-change="
              (sortingOption) => {
                searchStore.setSortingOption(sortingOption)
                $emit('refreshSearchData')
                useUpdateQuery()
              }
            "
          />
        </div>
      </div>
    </div>
    <LazyAccommodationListA
      :meta="searchData?.data.meta"
      :loading="searchPending"
      :accommodations="accommodationStore.getFilteredAccommodations"
      :adjacent="accommodationStore.getFilteredAdjacent"
      :alternatives="accommodationStore.getFilteredAlternatives"
      :occupied="searchData?.data.occupied"
      @open-info-window="
        (accommodation: Accommodation) => {
          $emit('updateSelectedAccommodation', accommodation)
          $emit('showAccommodationInfo')
        }
      "
      @book-now="
        (booking: AccommodationBookInfo) => {
          bookingStore.initiateBooking(
            booking.accommodationId,
            booking.arrivalDate,
            booking.duration,
            booking.isFromPrice,
            siteData?.data,
            false,
          )
        }
      "
    />
  </div>
</template>
